import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap, Pause, Play, Sparkles, Crown, Lock, ArrowRight, Home, Facebook, Instagram,
  Search, Target, Wallet, ChevronDown, CheckCircle2, Rocket, Wand2, Info,
  AlertTriangle, X, Repeat, TrendingUp, Power,
} from "lucide-react";
import GatedPage, { usePaywall } from "./GatedPage";
import WelcomeBanner from "../../components/WelcomeBanner";
import CityAutocomplete from "../../components/CityAutocomplete";
import { useAuth, apiClient } from "../../lib/auth";

export default function LeadGenerator() {
  return (
    <GatedPage featureName="Lead Engine">
      <LeadEngineInner />
    </GatedPage>
  );
}

// Single-budget allocation — Newground decides the split; users never do.
const GOAL_SPLIT = { buyers: [100, 0], sellers: [0, 100], balanced: [50, 50], mostly_buyers: [70, 30], mostly_sellers: [30, 70] };
function allocation(goal, hasListings) {
  let [b, s] = GOAL_SPLIT[goal] || [50, 50];
  let l = 0;
  if (hasListings) { l = 20; b = Math.round(b * 0.8); s = Math.round(s * 0.8); const sum = b + s + l; if (sum !== 100) b += 100 - sum; }
  return [
    { label: "Buyers", pct: b, color: "#1D6AFF" },
    { label: "Sellers", pct: s, color: "#F59E0B" },
    { label: "Listings", pct: l, color: "#10B981" },
  ].filter((x) => x.pct > 0);
}

// Budget-based cap on how many listings may actively spend at once. Hard cap 3.
function listingCap(budget) {
  if (budget >= 2000) return 3;
  if (budget >= 1000) return 2;
  return 1; // $500–$999
}

const GOAL_LABEL = { buyers: "Buyer Leads", sellers: "Seller Leads", balanced: "Balanced", mostly_buyers: "Mostly Buyers", mostly_sellers: "Mostly Sellers" };
const BUDGET_PRESETS = [750, 1500, 3000, 5000];
const CHANNELS = [
  { name: "Meta", icon: Facebook, color: "#1877F2" },
];

const SAMPLE_ENGINE = { market: "Beverly Hills, CA", budget: 1500, goal: "balanced", running: true, autoOptimize: true };

function LeadEngineInner() {
  const { user, updateUser } = useAuth();
  const { locked, showUpgrade } = usePaywall();
  const navigate = useNavigate();
  const location = useLocation();
  const isElite = user?.plan === "elite";

  const saved = locked ? SAMPLE_ENGINE : (user?.engine || {});
  const [market, setMarket] = useState(saved.market || user?.target_city || "");
  const [budget, setBudget] = useState(saved.budget || 1500);
  const [goal, setGoal] = useState(saved.goal || (isElite ? "balanced" : "buyers"));
  const [showDetails, setShowDetails] = useState(false);
  const [listings, setListings] = useState([]);
  const [limitModal, setLimitModal] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [replaceMode, setReplaceMode] = useState(false);
  const [budgetPulse, setBudgetPulse] = useState(false);
  const [showAllListings, setShowAllListings] = useState(false);
  const budgetRef = useRef(null);

  useEffect(() => {
    if (locked) { setListings([{ id: "s1", title: "742 Evergreen Terrace", city: "Beverly Hills, CA" }, { id: "s2", title: "88 Marina Vista", city: "Sausalito, CA" }]); return; }
    let on = true;
    apiClient.get("/creatives/library").then(({ data }) => { if (on) setListings(data.items || []); }).catch(() => {});
    return () => { on = false; };
  }, [locked]);

  const running = locked ? true : !!user?.engine?.running;
  const activeIds = locked ? ["s1"] : (user?.engine?.activeListings || []);
  const cap = listingCap(budget);
  const activeListingItems = listings.filter((l) => activeIds.includes(l.id));
  const activeCount = activeListingItems.length;
  const overLimit = activeCount > cap;
  const hasListings = activeCount > 0;
  const splits = allocation(goal, hasListings);
  const orderedListings = [...listings].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
  const visibleListings = showAllListings ? orderedListings : orderedListings.slice(0, 5);
  const dirty = !locked && (market !== (saved.market || "") || budget !== (saved.budget || 1500) || goal !== (saved.goal || (isElite ? "balanced" : "buyers")));

  const persist = (patch) => {
    if (locked) { showUpgrade("your Lead Engine"); return; }
    updateUser({ engine: { market, budget, goal, running: user?.engine?.running || false, autoOptimize: true, ...(user?.engine || {}), ...patch } });
  };
  const saveConfig = () => persist({ market, budget, goal });
  const persistListings = (ids) => persist({ market, budget, goal, activeListings: ids });
  const toggleListing = (id) => {
    if (locked) { showUpgrade("your Lead Engine"); return; }
    if (activeIds.includes(id)) { persistListings(activeIds.filter((x) => x !== id)); return; }
    if (activeCount >= cap) { setPendingId(id); setReplaceMode(false); setLimitModal(true); return; }
    persistListings([...activeIds, id]);
  };
  const confirmReplace = (removeId) => {
    persistListings([...activeIds.filter((x) => x !== removeId), pendingId]);
    setLimitModal(false); setReplaceMode(false); setPendingId(null);
  };
  const closeLimitModal = () => { setLimitModal(false); setReplaceMode(false); setPendingId(null); };
  const goIncreaseBudget = () => {
    closeLimitModal();
    budgetRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setBudgetPulse(true); setTimeout(() => setBudgetPulse(false), 1800);
  };
  // Handle hand-off from Creative Maker (Promote with Lead Engine / Increase budget).
  const handledNav = useRef(false);
  useEffect(() => {
    const st = location.state;
    if (locked || handledNav.current || !st) return;
    if (st.focusBudget) {
      handledNav.current = true;
      navigate(location.pathname, { replace: true, state: null });
      setTimeout(() => goIncreaseBudget(), 400);
    } else if (st.activateListingId && listings.length) {
      handledNav.current = true;
      const id = st.activateListingId;
      navigate(location.pathname, { replace: true, state: null });
      setTimeout(() => toggleListing(id), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, listings, locked]);
  const toggleRunning = () => {
    if (locked) { showUpgrade("your Lead Engine"); return; }
    if (!market.trim()) { showUpgrade("Add your market first"); return; }
    const next = !running;
    persist({ market, budget, goal, running: next, startedAt: next && !user?.engine?.startedAt ? new Date().toISOString() : user?.engine?.startedAt });
  };
  const selectGoal = (g) => {
    if (!isElite && g !== "buyers") { showUpgrade("Seller lead campaigns"); return; }
    if (locked) { showUpgrade("your Lead Engine"); return; }
    setGoal(g);
  };

  return (
    <div data-testid="lead-generator">
      <WelcomeBanner subtitle={null} />

      {/* ── STATUS HEADER ── */}
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-r from-[#1D6AFF]/[0.06] via-white to-emerald-50/40 p-6 sm:p-7 shadow-sm mb-6">
        <div className="pointer-events-none absolute -top-20 -right-16 w-72 h-72 rounded-full bg-[#1D6AFF]/10 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#1D6AFF]/10 border border-[#1D6AFF]/25 flex items-center justify-center shrink-0">
              <Zap className="w-6 h-6 text-[#1D6AFF]" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 mb-1.5">
                <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Lead Engine</h1>
                <StatusPill running={running} />
              </div>
              <p className="text-sm text-slate-500 max-w-md leading-relaxed">
                Tell Newground what business you want and give it one budget. Newground handles the rest, automatically.
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1" data-testid="auto-optimization-badge">
                <Wand2 className="w-3 h-3" /> Automatic Optimization — ON
              </div>
            </div>
          </div>
          <button data-testid="engine-toggle-btn" onClick={toggleRunning}
            className={`shrink-0 inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-3.5 font-bold transition-all shadow-sm hover:-translate-y-0.5 ${running ? "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50" : "text-white bg-gradient-to-r from-[#1D6AFF] to-[#2E7DFF] shadow-[0_16px_36px_-12px_rgba(29,106,255,0.6)]"}`}>
            {running ? <><Pause className="w-4 h-4" /> Pause Engine</> : <><Play className="w-4 h-4" /> Start Engine</>}
          </button>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ── SETUP (left) ── */}
        <div className="lg:col-span-7 space-y-6">
          <Card testId="engine-setup-card" icon={Target} title="What should Newground go find?">
            {/* Market */}
            <div className="mb-5">
              <CityAutocomplete value={market} onChange={setMarket} label="Your market"
                placeholder="e.g. Austin, TX" inputTestId="engine-market-input" suggestionsTestId="engine-market-suggestions" />
            </div>

            {/* Goal */}
            <div className="mb-5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Lead goal</div>
              {isElite ? (
                <div className="flex flex-wrap gap-2" data-testid="engine-goal-elite">
                  {["buyers", "mostly_buyers", "balanced", "mostly_sellers", "sellers"].map((g) => (
                    <GoalPill key={g} active={goal === g} onClick={() => selectGoal(g)} label={GOAL_LABEL[g]} testId={`goal-${g}`} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="engine-goal-pro">
                  <GoalPill active={goal === "buyers"} onClick={() => selectGoal("buyers")} label="Buyer Leads" testId="goal-buyers" />
                  <button data-testid="goal-sellers-locked" onClick={() => showUpgrade("Seller lead campaigns")}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 text-amber-700 px-4 py-2.5 text-sm font-semibold hover:bg-amber-100 transition-colors">
                    <Lock className="w-3.5 h-3.5" /> Seller Leads
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 border border-amber-200 rounded-full px-1.5 py-0.5"><Crown className="w-2.5 h-2.5" /> Elite</span>
                  </button>
                </div>
              )}
            </div>

            {/* Budget */}
            <div ref={budgetRef} className={`rounded-xl transition-all ${budgetPulse ? "ring-2 ring-[#1D6AFF] ring-offset-2 p-2 -m-2" : ""}`}>
              <div className="flex items-end justify-between mb-2">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Monthly marketing budget</div>
                <div className="font-display text-2xl font-extrabold text-slate-900 tabular-nums" data-testid="engine-budget-value">${budget.toLocaleString()}<span className="text-sm font-semibold text-slate-400">/mo</span></div>
              </div>
              <input data-testid="engine-budget-slider" type="range" min={500} max={10000} step={100} value={budget}
                onChange={(e) => (locked ? showUpgrade("your Lead Engine") : setBudget(Number(e.target.value)))}
                className="w-full accent-[#1D6AFF] cursor-pointer" />
              <div className="flex flex-wrap gap-2 mt-3">
                {BUDGET_PRESETS.map((p) => (
                  <button key={p} data-testid={`budget-preset-${p}`} onClick={() => (locked ? showUpgrade("your Lead Engine") : setBudget(p))}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${budget === p ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                    ${p.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            {dirty && (
              <button data-testid="engine-save-btn" onClick={saveConfig}
                className="mt-6 inline-flex items-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl px-5 py-2.5 text-sm transition-colors">
                <CheckCircle2 className="w-4 h-4" /> Save changes
              </button>
            )}
          </Card>

          {/* Listing campaigns */}
          <Card testId="engine-listings-card" icon={Home} title="Active listing campaigns"
            action={<button data-testid="promote-listing-link" onClick={() => navigate("/dashboard/creative")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1D6AFF] hover:text-[#1552C6] transition-colors">Promote a Listing <ArrowRight className="w-4 h-4" /></button>}>
            {listings.length > 0 ? (
              <div className="space-y-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span data-testid="listing-active-count" className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{activeCount} of {cap} active</span>
                  <span className="text-[11px] text-slate-400">Budget supports {cap} at once</span>
                </div>

                {overLimit && (
                  <div data-testid="listing-overlimit-banner" className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3 mb-1">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-[12px] text-amber-800 leading-relaxed">
                      You have more listings promoting than your budget supports ({activeCount} active, limit {cap}). Pause one or <button onClick={goIncreaseBudget} className="font-semibold underline hover:text-amber-900">increase your budget</button> to keep them all running.
                    </p>
                  </div>
                )}

                {visibleListings.map((l, i) => {
                  const title = l.property?.address || l.title || l.name || "Listing ad";
                  let sub = l.property?.city || l.city || "";
                  if (!sub || sub === title) sub = [l.template, l.size].filter(Boolean).join(" · ") || "Promoting from your budget";
                  const isActive = activeIds.includes(l.id);
                  return (
                  <div key={l.id || i} data-testid="engine-listing-item" className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-2 pr-2.5">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-200 shrink-0">
                      {l.thumb ? <img src={l.thumb} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Home className="w-4 h-4 text-slate-400" /></div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900 truncate">{title}</div>
                      <div className="text-[11px] text-slate-500 truncate">{sub}</div>
                    </div>
                    {isActive ? (
                      <button data-testid={`listing-toggle-${l.id}`} onClick={() => toggleListing(l.id)} title="Pause this listing"
                        className="group shrink-0 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600">
                        <span className="relative flex h-1.5 w-1.5 group-hover:hidden"><span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" /></span>
                        <Pause className="w-3 h-3 hidden group-hover:block" />
                        <span className="group-hover:hidden">Promoting</span>
                        <span className="hidden group-hover:inline">Pause</span>
                      </button>
                    ) : (
                      <button data-testid={`listing-toggle-${l.id}`} onClick={() => toggleListing(l.id)}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#1D6AFF]/30 bg-white text-[#1D6AFF] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors hover:bg-[#1D6AFF]/5">
                        <Power className="w-3 h-3" /> Activate
                      </button>
                    )}
                  </div>
                  );
                })}
                {listings.length > 5 && (
                  <button data-testid="listings-show-more" onClick={() => setShowAllListings((v) => !v)}
                    className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 text-xs font-semibold py-2 transition-colors">
                    {showAllListings ? (<>Show less <ChevronDown className="w-3.5 h-3.5 rotate-180" /></>) : (<>Show {listings.length - 5} more <ChevronDown className="w-3.5 h-3.5" /></>)}
                  </button>
                )}
                <p className="text-[11px] text-slate-400 pt-1 flex items-center gap-1.5"><Info className="w-3 h-3" /> Only active listings spend budget. Newground balances them with your buyer &amp; seller leads automatically.</p>
              </div>
            ) : (
              <div className="text-center py-7">
                <span className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3"><Home className="w-5 h-5 text-slate-400" /></span>
                <p className="text-sm text-slate-500 max-w-xs mx-auto leading-relaxed">No listings yet. Create one in Creative Maker and activate it here to promote using the same budget.</p>
                <button data-testid="promote-listing-empty-btn" onClick={() => navigate("/dashboard/creative")} className="mt-4 inline-flex items-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl px-5 py-2.5 text-sm transition-colors">
                  <Rocket className="w-4 h-4" /> Promote a Listing
                </button>
              </div>
            )}
          </Card>
        </div>

        {/* ── ALLOCATION + CHANNELS (right) ── */}
        <div className="lg:col-span-5 space-y-6">
          <Card testId="engine-allocation-card" icon={Sparkles} title="How Newground spends it">
            <div className="flex h-3 rounded-full overflow-hidden mb-4">
              {splits.map((s) => <div key={s.label} style={{ width: `${s.pct}%`, background: s.color }} className="transition-all duration-700" />)}
            </div>
            <div className="space-y-2 mb-4" data-testid="engine-allocation-legend">
              {splits.map((s) => (
                <div key={s.label} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />{s.label}</span>
                  <span className="font-bold text-slate-900 tabular-nums">{s.pct}%</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 leading-relaxed bg-slate-50 border border-slate-100 rounded-xl p-3">
              Newground automatically distributes your budget across the campaigns and channels producing the best opportunities.
            </p>
          </Card>

          <Card testId="engine-channels-card" icon={Radio2} title="Connected channels">
            <div className="grid grid-cols-1 gap-2.5">
              {CHANNELS.map((c) => (
                <div key={c.name} data-testid={`channel-${c.name.toLowerCase()}`} className="rounded-xl border border-slate-100 bg-slate-50 p-3 flex items-center gap-3 text-left">
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${c.color}14` }}><c.icon className="w-4 h-4" style={{ color: c.color }} /></span>
                  <span className="text-xs font-semibold text-slate-700 flex-1">{c.name}</span>
                  <span className={`text-[9px] font-bold uppercase tracking-wider ${running ? "text-emerald-600" : "text-slate-400"}`}>{running ? "Active" : "Ready"}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-3 text-center">Managed automatically — no setup required.</p>
          </Card>

          {/* View Details */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="engine-details">
            <button data-testid="engine-view-details-btn" onClick={() => setShowDetails((v) => !v)}
              className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
              <span className="flex items-center gap-2"><Info className="w-4 h-4 text-slate-400" /> View details</span>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showDetails ? "rotate-180" : ""}`} />
            </button>
            {showDetails && (
              <div className="px-5 pb-5 pt-1 space-y-2 border-t border-slate-100" data-testid="engine-details-content">
                <div className="text-[11px] text-slate-400 mb-2">Estimated monthly distribution (auto-managed):</div>
                {[{ k: "Meta & Instagram", w: 0.55 }, { k: "Google", w: 0.3 }, { k: "Retargeting & testing", w: 0.15 }].map((r) => (
                  <div key={r.k} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{r.k}</span>
                    <span className="font-semibold text-slate-900 tabular-nums">${Math.round(budget * r.w).toLocaleString()}/mo</span>
                  </div>
                ))}
                <p className="text-[11px] text-slate-400 pt-2 leading-relaxed">These numbers shift automatically as Newground finds what converts best in your market. You never manage individual campaigns.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Active-listing limit modal */}
      {limitModal && (
        <div data-testid="listing-limit-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={closeLimitModal}>
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />
          <motion.div initial={{ opacity: 0, scale: 0.94, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <button data-testid="listing-limit-close" onClick={closeLimitModal} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"><X className="w-4 h-4" /></button>
            <div className="p-6 sm:p-7">
              <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mb-4"><AlertTriangle className="w-6 h-6 text-amber-500" /></div>
              {!replaceMode ? (
                <>
                  <h3 className="font-display text-xl font-bold text-slate-900 mb-2">You've reached your active listing limit</h3>
                  <p data-testid="listing-limit-message" className="text-sm text-slate-600 leading-relaxed mb-6">
                    Your current budget supports up to <b className="text-slate-900">{cap} active listing{cap === 1 ? "" : "s"}</b> while Newground continues generating buyer and seller opportunities.
                  </p>
                  <div className="space-y-2.5">
                    <button data-testid="listing-limit-replace" onClick={() => setReplaceMode(true)}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-slate-800 font-semibold px-5 py-3 text-sm hover:bg-slate-50 transition-colors">
                      <Repeat className="w-4 h-4" /> Replace an active listing
                    </button>
                    <button data-testid="listing-limit-increase-budget" onClick={goIncreaseBudget}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl text-white font-semibold px-5 py-3 text-sm bg-gradient-to-r from-[#1D6AFF] to-[#2E7DFF] shadow-[0_14px_30px_-12px_rgba(29,106,255,0.6)] hover:-translate-y-0.5 transition-all">
                      <TrendingUp className="w-4 h-4" /> Increase marketing budget
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="font-display text-xl font-bold text-slate-900 mb-2">Choose a listing to pause</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5">Pausing one makes room for the new listing. You can reactivate it anytime.</p>
                  <div className="space-y-2.5 max-h-64 overflow-y-auto">
                    {activeListingItems.map((l) => {
                      const title = l.property?.address || l.title || l.name || "Listing ad";
                      const sub = l.property?.city || l.city || "Promoting";
                      return (
                        <div key={l.id} data-testid={`listing-replace-item-${l.id}`} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-2 pr-2.5">
                          <div className="w-11 h-11 rounded-lg overflow-hidden bg-slate-200 shrink-0">{l.thumb ? <img src={l.thumb} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Home className="w-4 h-4 text-slate-400" /></div>}</div>
                          <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-slate-900 truncate">{title}</div><div className="text-[11px] text-slate-500 truncate">{sub}</div></div>
                          <button data-testid={`listing-replace-confirm-${l.id}`} onClick={() => confirmReplace(l.id)}
                            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#1D6AFF] text-white px-3 py-1.5 text-[11px] font-bold hover:bg-[#1552C6] transition-colors">
                            <Repeat className="w-3 h-3" /> Swap in
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <button data-testid="listing-limit-back" onClick={() => setReplaceMode(false)} className="mt-5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">Back</button>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ running }) {
  return running ? (
    <span data-testid="engine-status-pill" className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
      <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
      Running
    </span>
  ) : (
    <span data-testid="engine-status-pill" className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1">
      <span className="w-2 h-2 rounded-full bg-slate-400" /> Paused
    </span>
  );
}

function GoalPill({ active, onClick, label, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`rounded-xl px-4 py-2.5 text-sm font-semibold border transition-all ${active ? "bg-[#1D6AFF] text-white border-[#1D6AFF] shadow-[0_8px_20px_-8px_rgba(29,106,255,0.6)]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
      {label}
    </button>
  );
}

function Card({ testId, icon: Icon, title, action, children }) {
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-[#1D6AFF]/10 flex items-center justify-center"><Icon className="w-4 h-4 text-[#1D6AFF]" /></span>
          <h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// small alias so we can use a distinct icon name without another import line
function Radio2(props) { return <Zap {...props} />; }
